import { useEffect, useState } from "react";
import axios from "axios";
import { Cookie, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((cfg) => {
  const t = typeof window !== "undefined" ? localStorage.getItem("mra_jwt") : null;
  if (t) cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${t}` };
  return cfg;
});

const pct = (rate) => `${Math.round((rate || 0) * 100)}%`;
const dayLabel = (iso) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "narrow" });

/**
 * ConsentStatsWidget — how many visitors opt in to analytics cookies.
 * Reads GET /api/admin/consent/stats (unique browsers, not clicks).
 */
export const ConsentStatsWidget = () => {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    client.get("/admin/consent/stats")
      .then((r) => setStats(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Couldn't load consent stats."));
  }, []);

  const all = stats?.all_time;
  const week = stats?.last_7_days;
  const max = Math.max(1, ...(stats?.daily || []).map((d) => d.accepted + d.declined));

  return (
    <div className="rounded-2xl border border-border bg-bone px-5 py-4" data-testid="consent-stats-widget">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-yellow text-ink">
            <Cookie className="h-4 w-4" strokeWidth={2} />
          </span>
          <div>
            <p className="font-semibold text-ink">Cookie consent</p>
            <p className="mt-0.5 text-[12px] text-olive">Visitors who accepted analytics, counted once per browser.</p>
          </div>
        </div>
        {!stats && !err && <Loader2 className="h-4 w-4 animate-spin text-olive" />}
      </div>

      {err && <p className="mt-3 text-[13px] text-scarlet" data-testid="consent-stats-error">{err}</p>}

      {stats && (
        <div className="mt-4 grid gap-4 sm:grid-cols-[auto_1fr] sm:items-end">
          <div className="flex items-end gap-6">
            <div>
              <p className="font-serif text-5xl leading-none text-ink" data-testid="consent-accept-rate">{pct(all.accept_rate)}</p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-olive">opt-in · all time</p>
            </div>
            <dl className="grid grid-cols-2 gap-x-5 gap-y-1 text-[12px] text-olive">
              <dt>Accepted</dt><dd className="text-right font-mono text-ink" data-testid="consent-accepted">{all.accepted}</dd>
              <dt>Essential only</dt><dd className="text-right font-mono text-ink" data-testid="consent-declined">{all.declined}</dd>
              <dt>Last 7 days</dt><dd className="text-right font-mono text-ink" data-testid="consent-week">{week.total ? `${pct(week.accept_rate)} of ${week.total}` : "—"}</dd>
              <dt>Changed mind</dt><dd className="text-right font-mono text-ink" data-testid="consent-changes">{stats.changes}</dd>
            </dl>
          </div>
          <div>
            <div className="flex h-14 items-end gap-1" data-testid="consent-daily-chart" aria-label="Decisions per day, last 14 days">
              {stats.daily.map((d) => {
                const total = d.accepted + d.declined;
                const h = total ? Math.max(6, Math.round((total / max) * 56)) : 2;
                const accH = total ? Math.round((d.accepted / total) * h) : 0;
                return (
                  <div key={d.date} className="flex flex-1 flex-col justify-end" title={`${d.date}: ${d.accepted} accepted · ${d.declined} essential only`}>
                    <div className="flex flex-col justify-end overflow-hidden rounded-sm bg-ink/10" style={{ height: h }}>
                      <div className="w-full bg-grass" style={{ height: accH }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-1 flex gap-1 text-[9px] uppercase text-olive/70">
              {stats.daily.map((d) => <span key={d.date} className="flex-1 text-center">{dayLabel(d.date)}</span>)}
            </div>
            <p className="mt-1 text-[11px] text-olive">
              <span className="mr-1 inline-block h-2 w-2 rounded-sm bg-grass align-middle" /> accepted ·
              <span className="mx-1 inline-block h-2 w-2 rounded-sm bg-ink/10 align-middle" /> essential only · via banner {stats.accepted_via_banner}, via preferences {stats.accepted_via_preferences}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
